/* eslint-disable max-len */
import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import { v4 as uuid } from 'uuid';

import AsyncTable, { Column, RequestPage } from '../../../../../../components/AsyncTable';
import Modal from '../../../../../../components/Modal';
import {
  backendErrorNotification,
  frontendNotification,
} from '../../../../../../components/Notification';
import SelectAsyncJSX from '../../../../../../components/SelectAsync';
import BasicDateTimePicker from '../../../../../../components/SelectDateTime';
import { ActionButtonsContainer } from '../../../../../../components/TablePaginator/styles';
import { SMARTSELL_ADMIN_ENVIRONMENT } from '../../../../../../constants/smartsell';
import HttpStatus from '../../../../../../enums/httpStatus';
import { PreviewIcon } from '../../../../../../icons';
import {
  customersApi,
  sellersApi,
  visitHistoryApi,
  visitScheduledAPI,
} from '../../../../../../services/requests';
import { ScheduledVisit, VisitOrigin } from '../../../../../../types';
import { VisitHistory } from '../../../../../../types/apiResponse/visitHistory';
import { ApiResPaginated } from '../../../../../../types/apiResponseTypes';
import {
  getOptionsFromCustomers,
  getOptionsFromSellers,
} from '../../../../../../utils/getSelectOptions';
import ModalVisitHistoryDetail from './Components/ModalVisitHistoryDetail';

type Option = {
  value: any;
  label: string;
};

type Props = {
  seller: Option | Record<string, unknown>;
  visit: ScheduledVisit | null;
  initialDate: Date;
  closeModal: () => void;
  onSuccess: () => Promise<void>;
};

const parseDateTimeValue = (value?: string | null) => {
  if (!value) {
    return null;
  }

  return dayjs(value.replace(' ', 'T'));
};

const formatHistoryDateTime = (value?: string | null): string => {
  if (!value) {
    return '-';
  }

  return dayjs(value.replace(' ', 'T')).format('DD/MM/YYYY HH:mm:ss');
};

const getVisitOriginLabel = (origin?: VisitOrigin | null): string => {
  if (!origin || origin === SMARTSELL_ADMIN_ENVIRONMENT) {
    return t('smartsellAdmin') as string;
  }

  if (origin === 'EXTERNAL') {
    return t('EXTERNAL') as string;
  }

  return t(origin) as string;
};

const parseScheduledDate = (value?: string | null): Date => {
  if (!value) {
    return new Date();
  }

  return new Date(value.replace(' ', 'T'));
};

const isNonFutureScheduledVisit = (visit?: ScheduledVisit | null): boolean => {
  if (!visit?.scheduled_for) {
    return false;
  }

  return parseScheduledDate(visit.scheduled_for).getTime() <= Date.now();
};

const isScheduledDateTimeNotFuture = (value: unknown): boolean => {
  if (!value) {
    return false;
  }

  return !dayjs(value as dayjs.ConfigType).isAfter(dayjs());
};

const getCustomerLabel = (visit: ScheduledVisit) => {
  const code = visit.customer?.CodigoCliente ?? visit.CodigoCliente;
  const name = visit.customer?.RazaoSocial ?? '-';

  return `${code} - ${name}`;
};

const ModalVisitScheduleDetail = ({
  seller,
  visit,
  initialDate,
  closeModal,
  onSuccess,
}: Props) => {
  const theme = useTheme();
  const [waitingRequest, setWaitingRequest] = useState(false);
  const [selectedSeller, setSelectedSeller] = useState<Option | Record<string, unknown>>({});
  const [customer, setCustomer] = useState<Option | Record<string, unknown>>({});
  const [scheduleId, setScheduleId] = useState('');
  const [scheduledFor, setScheduledFor] = useState<any>(null);
  const [note, setNote] = useState('');
  const [visitHistories, setVisitHistories] = useState<VisitHistory[]>([]);
  const [selectedVisitHistory, setSelectedVisitHistory] = useState<VisitHistory | null>(null);
  const [showVisitHistoryDetail, setShowVisitHistoryDetail] = useState(false);
  const minimumAllowedScheduledFor = dayjs().add(1, 'minute').startOf('minute');
  const isVisitLocked = isNonFutureScheduledVisit(visit);
  const isSelectedDateTimeInvalid = isScheduledDateTimeNotFuture(scheduledFor);
  const trimmedScheduleId = scheduleId.trim();

  const handleViewVisitHistory = (history: VisitHistory) => {
    setSelectedVisitHistory(history);
    setShowVisitHistoryDetail(true);
  };

  const historyActionsButtons = (history: VisitHistory) => (
    <ActionButtonsContainer type={4}>
      <PreviewIcon title={t('view')} onClick={() => handleViewVisitHistory(history)} />
    </ActionButtonsContainer>
  );

  const visitHistoryColumns: Column<VisitHistory>[] = [
    {
      label: t('id') as string,
      accessor: 'id',
    },
    {
      label: t('type') as string,
      accessor: 'type',
    },
    {
      label: t('registeredAt') as string,
      accessor: 'registered_at',
      Cell: (history) => formatHistoryDateTime(history.registered_at),
    },
    {
      label: t('note') as string,
      accessor: 'observation',
    },
    {
      label: t('actions') as string,
      headerCellStyle: {
        textAlign: 'center',
      },
      Cell: historyActionsButtons,
    },
  ];

  const getVisitHistoryItems: RequestPage = async ({ search, page, quantityPerPage }) => {
    if (!visit?.id) {
      return {
        data: [],
        hasMore: false,
        totalPage: 0,
      };
    }

    const source = visitHistoryApi.axios.CancelToken.source();

    try {
      const query = `?search=${search}&page=${page}&quantityPerPage=${quantityPerPage}&filter[visit_scheduled_id]=${encodeURIComponent(visit.id)}`;

      visitHistoryApi.query = query;

      const res = await visitHistoryApi.index(source.token);

      if (res.status !== HttpStatus.OK) {
        throw res;
      }

      const { data, paginated }: ApiResPaginated<VisitHistory[]> = res.data;

      return {
        data,
        hasMore: data.length >= quantityPerPage,
        totalPage: paginated.totalPage,
      };
    } catch (err) {
      if (!visitHistoryApi.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }

      return {
        data: [],
        hasMore: false,
        totalPage: 0,
      };
    }
  };

  useEffect(() => {
    setSelectedSeller(seller);

    if (visit) {
      setScheduleId(visit.id);
      setCustomer({
        value: visit.CodigoCliente,
        label: getCustomerLabel(visit),
      });
      setScheduledFor(parseDateTimeValue(visit.scheduled_for));
      setNote(visit.obs ?? '');
      return;
    }

    setCustomer({});
    setScheduleId(uuid());
    setScheduledFor(dayjs(initialDate));
    setNote('');
  }, [visit, initialDate, seller]);

  useEffect(() => {
    if (!visit) {
      setCustomer({});
    }
  }, [selectedSeller, visit]);

  const handleSubmit = async () => {
    if (visit && isVisitLocked) {
      frontendNotification({
        message: t('cannotUpdatePastScheduledVisit'),
        type: 'error',
      });

      return;
    }

    if (isSelectedDateTimeInvalid) {
      frontendNotification({
        message: t('cannotSchedulePastScheduledVisit'),
        type: 'error',
      });

      return;
    }

    setWaitingRequest(true);

    try {
      const payload = {
        ...(visit ? {} : { id: trimmedScheduleId }),
        CodigoVendedor: selectedSeller.value,
        CodigoCliente: customer.value,
        routine_id: visit?.routine_id ?? null,
        origin: visit?.origin ?? SMARTSELL_ADMIN_ENVIRONMENT,
        scheduled_for: dayjs(scheduledFor).format('YYYY-MM-DD HH:mm:ss'),
        obs: note.trim() || null,
      };

      const res = visit
        ? await visitScheduledAPI.update(payload, visit.id)
        : await visitScheduledAPI.store(payload);

      if (res.status !== HttpStatus.OK && res.status !== HttpStatus.CREATED) {
        throw res;
      }

      await onSuccess();

      frontendNotification({
        message: visit ? t('successfullyUpdated') : t('successfullyCreated'),
        type: 'success',
      });

      closeModal();
    } catch (err) {
      if (!visitScheduledAPI.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
    }

    setWaitingRequest(false);
  };

  const handleDelete = async () => {
    if (!visit) {
      return;
    }

    if (isVisitLocked) {
      frontendNotification({
        message: t('cannotDeletePastScheduledVisit'),
        type: 'error',
      });

      return;
    }

    setWaitingRequest(true);

    try {
      const res = await visitScheduledAPI.delete(visit.id);

      if (res.status !== HttpStatus.OK) {
        throw res;
      }

      await onSuccess();

      frontendNotification({
        message: t('successfullyDeleted'),
        type: 'success',
      });

      closeModal();
    } catch (err) {
      if (!visitScheduledAPI.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
    }

    setWaitingRequest(false);
  };

  return (
    <>
      <Modal
        title={visit ? t('scheduledVisitDetails') : t('newScheduledVisit')}
        action={handleSubmit}
        showModal
        closeModal={closeModal}
        actionNameBtn={visit ? t('save') : t('create')}
        isLoading={waitingRequest}
        bodyStyle={{ overflow: 'auto', width: '100%' }}
        disableActionBtn={
          (!visit && !trimmedScheduleId)
          || !selectedSeller.value
          || !customer.value
          || !scheduledFor
          || isSelectedDateTimeInvalid
          || !!(visit && isVisitLocked)
        }
        additionalActions={
          visit
            ? [
              {
                name: t('remove') as string,
                onClick: handleDelete,
                style: { maxWidth: '120px' },
                disable: isVisitLocked,
              },
            ]
            : []
        }
      >
        <div>
          <div className='row'>
            <div className='itemFormWithLabel'>
              <label className='label'>{t('scheduledFor') as string}</label>
              <BasicDateTimePicker
                value={scheduledFor}
                onChange={setScheduledFor}
                disabled={waitingRequest || !!(visit && isVisitLocked)}
                disablePast
                minDateTime={minimumAllowedScheduledFor}
                label=""
              />
            </div>

            {visit ? (
              <div className='itemFormWithLabel'>
                <label className='label'>{t('scheduleOrigin') as string}</label>
                <input
                  type="text"
                  value={getVisitOriginLabel(visit?.origin ?? SMARTSELL_ADMIN_ENVIRONMENT)}
                  onChange={() => { }}
                />
              </div>
            ) : null}
          </div>

          <div className='row'>
            <div className='itemFormWithLabel'>
              <label className='label'>
                {(visit ? t('seller') : t('selectSeller')) as string}
              </label>
              {visit ? (
                <input type="text" value={seller.label as string} onChange={() => { }} />
              ) : (
                <SelectAsyncJSX
                  value={selectedSeller}
                  onChange={(value: any) => setSelectedSeller(value ?? {})}
                  request={sellersApi}
                  reqResponseToOption={getOptionsFromSellers}
                  disabled={waitingRequest}
                />
              )}
            </div>

            <div className='itemFormWithLabel'>
              <label className='label'>
                {(visit ? t('customer') : t('sellersCustomers')) as string}
              </label>
              {visit ? (
                <input type="text" value={customer.label as string} onChange={() => { }} />
              ) : (
                <SelectAsyncJSX
                  value={customer}
                  onChange={(value: any) => setCustomer(value ?? {})}
                  request={customersApi}
                  reqResponseToOption={getOptionsFromCustomers}
                  query={`sellerCode=${selectedSeller.value}`}
                  cacheUniqs={[selectedSeller.value]}
                  disabled={waitingRequest || !selectedSeller.value}
                />
              )}
            </div>
          </div>

          <div className='row'>
            <div className='itemFormWithLabel'>
              <label className='label'>{t('observation') as string}</label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={waitingRequest || !!(visit && isVisitLocked)}
                style={{
                  minHeight: '3rem',
                  padding: '0.75rem 0.85rem',
                  border: `1px solid ${theme.colors.onBackground}40`,
                  borderRadius: '0.75rem',
                  background: theme.colors.surface,
                  color: theme.colors.text,
                  resize: 'vertical',
                }}
              />
            </div>
          </div>

          {isVisitLocked ? (
            <div className='row'>
              <div className='itemFormWithoutLabel'>
                <small style={{ color: theme.colors.negative }}>
                  {t('cannotUpdatePastScheduledVisit') as string}
                </small>
              </div>
            </div>
          ) : null}

          {!isVisitLocked && isSelectedDateTimeInvalid ? (
            <div className='row'>
              <div className='itemFormWithoutLabel'>
                <small style={{ color: theme.colors.negative }}>
                  {t('cannotSchedulePastScheduledVisit') as string}
                </small>
              </div>
            </div>
          ) : null}

          {visit ? (
            <div className='row'>
              <div className='itemFormWithoutLabel' style={{ width: '100%' }}>
                <div style={{ marginBottom: '0.75rem', fontWeight: 600 }}>
                  {t('visitHistoryLabel') as string}
                </div>

                <AsyncTable
                  tableName={t('visitHistory') as string}
                  columns={visitHistoryColumns}
                  value={visitHistories}
                  onChange={setVisitHistories}
                  requestPage={getVisitHistoryItems}
                  reqListener={[visit.id]}
                  defaultQuantityPerPage={5}
                  options={{
                    hideDownloads: true,
                    styles: {
                      primaryColor: `${theme.colors.surface}`,
                      secondaryColor: `${theme.colors.onSurface}`,
                      alternateRowColor: theme.colors.onSurfaceLight,
                      textColor: theme.colors.text,
                    },
                    quantityPerPageLabel: t('quantityPerPage'),
                    searchPlaceholder: t('search'),
                  }}
                />
              </div>
            </div>
          ) : null}
        </div>
      </Modal>

      <ModalVisitHistoryDetail
        showModal={showVisitHistoryDetail}
        closeModal={() => {
          setShowVisitHistoryDetail(false);
          setSelectedVisitHistory(null);
        }}
        history={selectedVisitHistory}
      />
    </>
  );
};
export default ModalVisitScheduleDetail;
